/* eslint-disable max-lines */
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Box, DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { FormProvider } from "react-hook-form";

import { useWorkplaceComments } from "../redesign/Workplace/Review/api/useWorkplaceComments";
import { PostShiftReviewHeader } from "./components/PostShiftReviewHeader";
import { useIsWorkplaceReviewUXUpdateEnabled } from "./hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { usePostShiftReviewNavigation } from "./hooks/usePostShiftReviewNavigation";
import { usePostShiftReviewSteps } from "./hooks/usePostShiftReviewSteps";
import { ChooseAnonymityStep } from "./steps/ChooseAnonymityStep";
import { CompareWorkplacesStep } from "./steps/CompareWorkplacesStep";
import { CompletionStep } from "./steps/CompletionStep";
import { ParkingQuestionStep } from "./steps/ParkingQuestionStep";
import { PeerReviewsStep } from "./steps/PeerReviewsStep";
import { QuestionStep } from "./steps/QuestionStep";
import { RateWorkplaceQuestionsStep } from "./steps/RateWorkplaceQuestionsStep";
import { RateWorkplaceStep } from "./steps/RateWorkplaceStep";
import { SelectReviewTypeStep } from "./steps/SelectReviewTypeStep";
import { VerifyEmploymentStep } from "./steps/VerifyEmploymentStep";
import { VideoReviewStep } from "./steps/VideoReviewStep";
import { WriteReviewStep } from "./steps/WriteReviewStep";
import {
  LEAVE_REVIEW_ENTRYPOINT,
  type PostShiftReviewFlowContentProps,
  type PostShiftReviewStep,
  QuestionTag,
  REVIEW_TYPE_TO_STEP,
} from "./types";
import { usePostShiftReviewForm } from "./usePostShiftReviewForm";

export function PostShiftReviewFlowContent({
  modalState,
  facility,
  onComplete,
  shiftId,
  questionMappings,
  isLoading = false,
  isError = false,
  onSubmitRating,
  onSubmitReview,
  onSubmitReviewError,
  onSubmitComparison,
  isDelayedReview = false,
  shift,
  comparisonPair,
  canSubmitEmploymentVerification,
  source,
}: PostShiftReviewFlowContentProps) {
  const { isEnabled: isWriteReviewUiUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  const shouldLoadPeerComments =
    isWriteReviewUiUpdateEnabled && source !== LEAVE_REVIEW_ENTRYPOINT.WORKPLACE_PROFILE;

  const { data: peerComments, isLoading: isLoadingPeerComments } = useWorkplaceComments(
    {
      workplaceId: facility.id,
      filter: { excludeSelfComments: true, sort: "-relevancyScore" },
      page: { size: 3 },
    },
    { enabled: shouldLoadPeerComments }
  );
  const hasPeerComments = isDefined(peerComments) && peerComments.length > 0;

  const { form } = usePostShiftReviewForm({
    onSubmitRating,
    onSubmitReview,
  });

  const hasComparisonPair = isDefined(comparisonPair);
  const { stepTypes, getStepConfig } = usePostShiftReviewSteps({
    questionMappings,
    isError,
    hasComparisonPair,
    canSubmitEmploymentVerification,
    hasPeerComments,
    source,
  });

  const { currentStep, handleStepAction, isSubmittingRatingLocally, isSubmittingReviewLocally } =
    usePostShiftReviewNavigation({
      stepTypes,
      form,
      onSubmitRating,
      onSubmitReview,
      onSubmitReviewError,
      onSubmitComparison,
      onComplete,
      closeModal: modalState.closeModal,
      shiftId,
      workplaceId: facility.id,
      source,
    });

  function getLoadingHeaderCurrentStep(): PostShiftReviewStep {
    if (!isWriteReviewUiUpdateEnabled) {
      return "pick-rating";
    }

    if (source === LEAVE_REVIEW_ENTRYPOINT.WORKPLACE_PROFILE) {
      return "rate-workplace-questions";
    }

    if (hasComparisonPair) {
      return "compare-workplaces";
    }

    return hasPeerComments ? "peer-reviews" : "rate-workplace-questions";
  }

  const loadingHeaderCurrentStep = getLoadingHeaderCurrentStep();
  const loadingHeaderComputedSteps: PostShiftReviewStep[] = isWriteReviewUiUpdateEnabled
    ? [
        ...(hasComparisonPair && source !== LEAVE_REVIEW_ENTRYPOINT.WORKPLACE_PROFILE
          ? ["compare-workplaces" as const]
          : []),
        ...(hasPeerComments && source !== LEAVE_REVIEW_ENTRYPOINT.WORKPLACE_PROFILE
          ? ["peer-reviews" as const]
          : []),
        "rate-workplace-questions",
        "select-review-type",
        "write-review",
        "choose-anonymity",
        ...(canSubmitEmploymentVerification ? (["verify-employment"] as const) : []),
        "end",
      ]
    : ["pick-rating", "write-review", "end"];

  const REVIEW_TYPE_STEPS: PostShiftReviewStep[] = ["write-review", "video-review"];
  const reviewType = form.watch("reviewType");
  const activeReviewStep: PostShiftReviewStep =
    (reviewType && REVIEW_TYPE_TO_STEP[reviewType]) ?? "write-review";

  const filteredStepTypes = stepTypes.filter(
    (step) => !REVIEW_TYPE_STEPS.includes(step) || step === activeReviewStep
  );

  const isLoadingFlow = isLoading || (shouldLoadPeerComments && isLoadingPeerComments);

  useLogEffect(
    APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_STEP_VIEWED,
    {
      step: currentStep,
      shiftId,
      source,
    },
    { enabled: modalState.modalIsOpen && !isLoadingFlow }
  );

  const headerCurrentStep = isLoadingFlow ? loadingHeaderCurrentStep : currentStep;
  const headerComputedSteps = isLoadingFlow ? loadingHeaderComputedSteps : filteredStepTypes;

  const currentStepConfig = getStepConfig(currentStep);

  return (
    <FormProvider {...form}>
      <FullScreenDialog
        modalState={modalState}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.tertiary,
          }),
        }}
      >
        <PostShiftReviewHeader
          currentStep={headerCurrentStep}
          computedSteps={[...headerComputedSteps]}
          onBackOrClose={handleStepAction}
        />

        {isLoadingFlow ? (
          <DialogContent>
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
              <Text>Loading...</Text>
            </Stack>
          </DialogContent>
        ) : (
          <>
            {isError && (
              <Alert severity="error" sx={{ my: 2 }}>
                Unable to load review questions. Some steps may be unavailable.
              </Alert>
            )}

            <Box
              sx={(theme) => ({
                flex: 1,
                display: "flex",
                flexDirection: "column",
                mt: 5,
                p: 0,
                backgroundColor: theme.palette.background.tertiary,
              })}
            >
              {currentStep === "compare-workplaces" && isDefined(comparisonPair) && (
                <CompareWorkplacesStep
                  workplaceA={comparisonPair.workplaceA}
                  workplaceB={comparisonPair.workplaceB}
                  shiftId={comparisonPair.shiftId}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "peer-reviews" && isDefined(peerComments) && (
                <PeerReviewsStep
                  workplaceId={facility.id}
                  workplaceName={facility.name}
                  comments={peerComments}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "rate-workplace-questions" &&
                isDefined(currentStepConfig?.consolidatedQuestions) && (
                  <RateWorkplaceQuestionsStep
                    facilityName={facility.name}
                    questions={currentStepConfig.consolidatedQuestions}
                    isSubmitting={isSubmittingReviewLocally}
                    onAction={handleStepAction}
                  />
                )}
              {currentStep === "pick-rating" && (
                <RateWorkplaceStep
                  facilityName={facility.name}
                  facilityPhoto={facility.photo}
                  isSubmitting={isSubmittingRatingLocally}
                  isDelayedReview={isDelayedReview}
                  shift={shift}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "select-review-type" && (
                <SelectReviewTypeStep workplaceName={facility.name} onAction={handleStepAction} />
              )}
              {currentStep === "write-review" && (
                <WriteReviewStep
                  isSubmitting={isSubmittingReviewLocally}
                  isDelayedReview={isDelayedReview}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "video-review" && (
                <VideoReviewStep
                  workplaceId={facility.id}
                  shiftId={shiftId}
                  source={source}
                  onAction={handleStepAction}
                />
              )}
              {(currentStep === QuestionTag.SOFTWARE ||
                currentStep === QuestionTag.SUPPLIES ||
                currentStep === QuestionTag.PATIENTS_PER_NURSE) &&
                isDefined(currentStepConfig) &&
                isDefined(currentStepConfig.questionType) && (
                  <QuestionStep
                    questionTag={currentStep}
                    questionText={currentStepConfig.questionText ?? ""}
                    questionType={currentStepConfig.questionType}
                    options={currentStepConfig.options}
                    numberConfig={currentStepConfig.numberConfig}
                    isSubmitting={isSubmittingReviewLocally}
                    onAction={handleStepAction}
                  />
                )}
              {currentStep === QuestionTag.PARKING && (
                <ParkingQuestionStep
                  isSubmitting={isSubmittingReviewLocally}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "choose-anonymity" && (
                <ChooseAnonymityStep
                  isSubmitting={isSubmittingReviewLocally}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "verify-employment" && (
                <VerifyEmploymentStep
                  isSubmitting={isSubmittingReviewLocally}
                  workplaceId={facility.id}
                  shiftId={shiftId}
                  source={source}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "end" && <CompletionStep onAction={handleStepAction} />}
            </Box>
          </>
        )}
      </FullScreenDialog>
    </FormProvider>
  );
}
/* eslint-enable max-lines */
